import type {
  CanonicalAISession,
  CanonicalBatchError,
  CanonicalPersistedAssetIds,
  CanonicalStageStatus,
  StoryboardReviewProjection,
  StoryboardStructureProjection,
} from '@/lib/ai-session/contracts/types';

export type BreakdownWorkspaceBatchError = Readonly<{
  batchIndex: number;
  message: string;
}>;

export type AssetWorkspaceSnapshot = Readonly<{
  batchErrors: BreakdownWorkspaceBatchError[];
  degraded: boolean;
  error: string | null;
  persistedAssetIds: CanonicalPersistedAssetIds | undefined;
  stageStatus: CanonicalStageStatus | null;
}>;

export type StoryboardWorkspaceSnapshot = Readonly<{
  batchErrors: BreakdownWorkspaceBatchError[];
  error: string | null;
  hasUnassociatedScene: boolean;
  missingSceneOrders: number[];
  persistedStoryboardIds: readonly string[] | undefined;
  recoverable: boolean;
  stageStatus: CanonicalStageStatus | null;
  review: StoryboardReviewProjection | undefined;
  structure: StoryboardStructureProjection | undefined;
}>;

function selectBatchErrors(
  batchErrors: readonly CanonicalBatchError[] | undefined
): BreakdownWorkspaceBatchError[] {
  if (!batchErrors) return [];
  return batchErrors.flatMap((item) => {
    const message = item.message?.trim() ?? '';
    return typeof item.batchIndex === 'number' && message
      ? [{ batchIndex: item.batchIndex, message }]
      : [];
  });
}

function selectError(error: string | undefined): string | null {
  const value = error?.trim() ?? '';
  return value || null;
}

export function selectAssetWorkspaceSnapshot(
  session: CanonicalAISession | null
): AssetWorkspaceSnapshot | null {
  if (session?.sessionType !== 3) return null;
  const payload = session.payload ?? {};
  const batchErrors = selectBatchErrors(payload.batchErrors);
  return {
    batchErrors,
    degraded: payload.degraded === true || batchErrors.length > 0,
    error: selectError(payload.error),
    persistedAssetIds: payload.persistedAssetIds,
    stageStatus: session.stageStatus,
  };
}

export function selectStoryboardWorkspaceSnapshot(
  session: CanonicalAISession | null
): StoryboardWorkspaceSnapshot | null {
  if (session?.sessionType !== 4) return null;
  const payload = session.payload ?? {};
  return {
    batchErrors: selectBatchErrors(payload.batchErrors),
    error: selectError(payload.error),
    hasUnassociatedScene: payload.storyboardDraftIntegrity?.hasUnassociatedScene === true,
    missingSceneOrders: Array.from(new Set(payload.missingSceneOrders ?? [])).sort(
      (left, right) => left - right
    ),
    persistedStoryboardIds: payload.persistedStoryboardIds,
    recoverable: payload.recoverable === true,
    stageStatus: session.stageStatus,
    review: payload.storyboardReview,
    structure: payload.storyboardStructure,
  };
}

export type WorkspacePreviewItem = Readonly<{
  id: string;
  group: 'characters' | 'locations' | 'props' | 'storyboards';
  title: string;
  description: string;
  ready: boolean;
  updating?: boolean;
  /** 已生成、等待原文复核；复核可删改，未就绪且不计数 */
  reviewing?: boolean;
  scene?: string;
  data: Readonly<Record<string, unknown>>;
}>;

export type WorkspacePreview = Readonly<{
  runId: string;
  revision: number;
  stage: 'asset' | 'storyboard';
  status: 'generating' | 'draft' | 'failed';
  total: number | null;
  items: readonly WorkspacePreviewItem[];
}>;

export function parseWorkspacePreview(value: unknown): WorkspacePreview | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  if (
    typeof record.runId !== 'string' ||
    !record.runId ||
    !Number.isInteger(record.revision) ||
    (record.revision as number) < 0 ||
    !['asset', 'storyboard'].includes(String(record.stage)) ||
    !['generating', 'draft', 'failed'].includes(String(record.status)) ||
    !(record.total === null || (Number.isInteger(record.total) && (record.total as number) >= 0)) ||
    !Array.isArray(record.items)
  )
    return null;
  const items: WorkspacePreviewItem[] = [];
  const seen = new Set<string>();
  for (const raw of record.items) {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
    const item = raw as Record<string, unknown>;
    if (
      typeof item.id !== 'string' ||
      !item.id ||
      typeof item.title !== 'string' ||
      typeof item.description !== 'string' ||
      typeof item.ready !== 'boolean' ||
      !['characters', 'locations', 'props', 'storyboards'].includes(String(item.group)) ||
      !item.data ||
      typeof item.data !== 'object' ||
      Array.isArray(item.data) ||
      (record.stage === 'storyboard') !== (item.group === 'storyboards')
    )
      return null;
    if (seen.has(item.id)) continue;
    seen.add(item.id);
    items.push({
      id: item.id,
      group: item.group as WorkspacePreviewItem['group'],
      title: item.title,
      description: item.description,
      ready: item.ready,
      updating: item.updating === true,
      ...(item.reviewing === true ? { reviewing: true } : {}),
      ...(typeof item.scene === 'string' ? { scene: item.scene } : {}),
      data: item.data as Record<string, unknown>,
    });
  }
  return {
    runId: record.runId,
    revision: record.revision as number,
    stage: record.stage as WorkspacePreview['stage'],
    status: record.status as WorkspacePreview['status'],
    total: record.total as number | null,
    items,
  };
}

/** 增量只在同一运行、精确基线上应用；完整快照仍是恢复权威。 */
export function applyWorkspacePreviewDelta(
  current: WorkspacePreview | null,
  value: unknown
): WorkspacePreview | null {
  if (!current || !value || typeof value !== 'object' || Array.isArray(value)) return null;
  const delta = value as Record<string, unknown>;
  const changed = parseWorkspacePreview(delta);
  if (!changed || changed.runId !== current.runId || changed.stage !== current.stage) return null;
  if (changed.revision <= current.revision) return current;
  if (
    delta.baseRevision !== current.revision ||
    !Array.isArray(delta.removedIds) ||
    !Array.isArray(delta.itemOrder)
  )
    return null;
  if (
    !delta.removedIds.every((id) => typeof id === 'string') ||
    !delta.itemOrder.every((id) => typeof id === 'string')
  )
    return null;
  const items = new Map(current.items.map((item) => [item.id, item]));
  for (const id of delta.removedIds) items.delete(id);
  for (const item of changed.items) items.set(item.id, item);
  const order = delta.itemOrder as string[];
  if (
    order.length !== items.size ||
    new Set(order).size !== items.size ||
    order.some((id) => !items.has(id))
  )
    return null;
  return { ...changed, items: order.map((id) => items.get(id)!) };
}

export function reconcileWorkspacePreview(
  current: WorkspacePreview | null,
  incoming: WorkspacePreview | null,
  beginRun = false
): WorkspacePreview | null {
  if (!incoming) return current;
  if (!current) return incoming;
  if (current.runId !== incoming.runId) return beginRun ? incoming : current;
  return incoming.revision > current.revision ? incoming : current;
}

/** 快照和 SSE 共用：只合并只读预览，不借预览回写正式成果、阶段或权限。 */
export function preserveWorkspacePreview(
  current: CanonicalAISession | null | undefined,
  incoming: CanonicalAISession,
  options?: { previewRunIdAtRequestStart: string | null }
): CanonicalAISession {
  if (!current || current.sessionId !== incoming.sessionId) return incoming;
  const previous = parseWorkspacePreview(current.payload.lastAgentProgress?.workspacePreview);
  const next = parseWorkspacePreview(incoming.payload.lastAgentProgress?.workspacePreview);
  // 请求期间本地已切到别的运行时，整份响应都已过期，不能只保住预览却回退阶段。
  const localRunChangedDuringRequest =
    options !== undefined && options.previewRunIdAtRequestStart !== (previous?.runId ?? null);
  if (localRunChangedDuringRequest) return current;
  if (
    current.sessionType === 4 &&
    incoming.sessionType === 4 &&
    next === null &&
    incoming.stageStatus !== 'running' &&
    Number(incoming.payload.storyboardVersion ?? 0) > Number(current.payload.storyboardVersion ?? 0)
  ) {
    // 恢复/保存形成新草稿版本，旧 Run 的预览不能覆盖新的权威草稿。
    return incoming;
  }
  const incomingUpdatedAt = Date.parse(incoming.updatedAt);
  const currentUpdatedAt = Date.parse(current.updatedAt);
  const incomingNotOlder = !(incomingUpdatedAt < currentUpdatedAt);
  const incomingStrictlyNewer = incomingUpdatedAt > currentUpdatedAt;
  // 本地已收到 started 的运行优先于权威读取里另一 run 的终态快照；该快照可能尚未追上 SSE。
  if (
    options !== undefined &&
    incomingNotOlder &&
    previous?.status === 'generating' &&
    !incomingStrictlyNewer &&
    (next === null || previous.runId !== next.runId)
  ) {
    return current;
  }
  // 只有带请求起点身份的权威读取可以发现其他页面启动的新运行。
  // mutation / stream patch 未提供 options，不得仅凭 updatedAt 切换 run。
  const authoritativeRefresh = options !== undefined && incomingNotOlder;
  const accepted = reconcileWorkspacePreview(previous, next, authoritativeRefresh);
  if (!accepted || accepted === next) return incoming;
  return {
    ...incoming,
    payload: {
      ...incoming.payload,
      lastAgentProgress: { ...incoming.payload.lastAgentProgress, workspacePreview: accepted },
    },
  } as CanonicalAISession;
}
